import React, { useState, useEffect } from 'react';
import { Search, Car, Truck, Bike, Bus, Hash, Loader2, Bookmark, AlertCircle, Wrench, Anchor, Sparkles } from 'lucide-react';
import { vehicleAPI } from '../api/client';
import apiClient from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import VehicleInfoTabs from '../components/VehicleInfoTabs';
import TechModeCard from '../components/TechModeCard';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const VEHICLE_TYPES = [
  { id: 'car', label: 'Cars', icon: Car, description: 'Passenger vehicles' },
  { id: 'truck', label: 'Trucks', icon: Truck, description: 'Pickup trucks' },
  { id: 'suv', label: 'SUVs', icon: Car, description: 'Sport utility vehicles' },
  { id: 'van', label: 'Vans', icon: Bus, description: 'Passenger & cargo vans' },
  { id: 'commercial', label: 'Commercial', icon: Truck, description: 'Heavy commercial trucks' },
  { id: 'motorcycle', label: 'Motorcycles', icon: Bike, description: 'Motorcycles & bikes' },
  { id: 'rv', label: 'RVs', icon: Bus, description: 'Recreational vehicles' },
  { id: 'equipment', label: 'Equipment', icon: Wrench, description: 'Heavy equipment' },
  { id: 'marine', label: 'Marine', icon: Anchor, description: 'Boats & outboard motors' },
];

export default function VehicleLookup() {
  const [vehicleType, setVehicleType] = useState('car');
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [typesConfig, setTypesConfig] = useState({});

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [trim, setTrim] = useState('');
  const [vin, setVin] = useState('');

  const [vehicleData, setVehicleData] = useState(null);
  const [techMode, setTechMode] = useState(() => {
    // Persist tech-mode toggle in localStorage so field techs don't have to re-enable each visit.
    try {
      return localStorage.getItem('vl_tech_mode') === '1';
    } catch (_) { return false; }
  });
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [loadingVin, setLoadingVin] = useState(false);

  useEffect(() => {
    loadYears();
    loadTypesConfig();
  }, []);

  useEffect(() => {
    if (vehicleType && typesConfig) {
      loadMakes();
      setSelectedMake('');
      setSelectedModel('');
      setModels([]);
      setVehicleData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, typesConfig]);

  useEffect(() => {
    if (selectedMake && selectedYear) {
      loadModels();
      setSelectedModel('');
      setVehicleData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMake, selectedYear]);

  const loadYears = async () => {
    setLoadingYears(true);
    try {
      const data = await vehicleAPI.getYears();
      setYears(data.years || []);
    } catch (error) {
      toast.error('Failed to load years');
    } finally {
      setLoadingYears(false);
    }
  };

  const loadTypesConfig = async () => {
    try {
      const data = await vehicleAPI.getTypes();
      setTypesConfig(data);
    } catch (error) {
      console.error('Error loading types config:', error);
    }
  };

  const loadMakes = async () => {
    setLoadingMakes(true);
    try {
      const typeMakes = typesConfig[vehicleType] || [];
      setMakes(typeMakes.map((name, idx) => ({ make_id: `${vehicleType}_${idx}`, make_name: name })));
    } catch (error) {
      toast.error('Failed to load makes');
    } finally {
      setLoadingMakes(false);
    }
  };

  const loadModels = async () => {
    if (!selectedMake || !selectedYear) return;
    setLoadingModels(true);
    try {
      const data = await vehicleAPI.getModels(selectedMake, selectedYear, vehicleType);
      setModels(data.models || []);
    } catch (error) {
      toast.error('Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleLookup = async () => {
    if (!selectedYear || !selectedMake || !selectedModel) {
      toast.error('Please select year, make, and model');
      return;
    }

    setLoadingLookup(true);
    setVehicleData(null);
    const loadingToast = toast.loading('Generating comprehensive vehicle data with AI...');

    try {
      const response = await apiClient.post('/api/vehicles/lookup', {
        year: parseInt(selectedYear),
        make: selectedMake,
        model: selectedModel,
        vehicle_type: vehicleType,
        trim: trim || null,
      });

      const data = response.data;
      setVehicleData(data);
      toast.dismiss(loadingToast);

      if (data.from_cache) {
        toast.success('Vehicle data loaded from cache!');
      } else if (data.data_source === 'ai_generated') {
        toast.success('Vehicle data generated by AI!');
      } else {
        toast.success('Vehicle data loaded');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Failed to load vehicle data');
    } finally {
      setLoadingLookup(false);
    }
  };

  const handleVinDecode = async () => {
    if (!vin || vin.length !== 17) {
      toast.error('Please enter a valid 17-character VIN');
      return;
    }

    setLoadingVin(true);
    try {
      const data = await vehicleAPI.decodeVIN(vin);
      if (data.error) {
        toast.error(data.error);
      } else {
        // Auto-fill form from VIN
        if (data.year) setSelectedYear(data.year.toString());
        if (data.make) setSelectedMake(data.make);
        if (data.model) setSelectedModel(data.model);
        if (data.trim) setTrim(data.trim);
        toast.success('VIN decoded! Click "Get Full Data" to continue.');
      }
    } catch (error) {
      toast.error('Failed to decode VIN');
    } finally {
      setLoadingVin(false);
    }
  };

  const handleReset = () => {
    setSelectedYear('');
    setSelectedMake('');
    setSelectedModel('');
    setTrim('');
    setModels([]);
    setVehicleData(null);
  };

  const handleSaveVehicle = async () => {
    if (!vehicleData) return;
    try {
      await vehicleAPI.create({
        year: parseInt(selectedYear),
        make: selectedMake,
        model: selectedModel,
        vehicle_type: vehicleType,
        trim: trim || null,
        ...vehicleData,
      });
      toast.success('Vehicle saved to your database!');
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="vehicle-lookup-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          Vehicle Lookup
          <Sparkles className="w-6 h-6 text-yellow-500" />
        </h1>
        <p className="text-muted-foreground">
          AI-powered comprehensive locksmith data: key blanks, programming, ECU info, pricing & more
        </p>
      </div>

      {/* Vehicle Type Tabs */}
      <Tabs value={vehicleType} onValueChange={setVehicleType}>
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar" data-testid="vehicle-type-tabs">
          {VEHICLE_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <TabsTrigger
                key={type.id}
                value={type.id}
                className="flex flex-col items-center gap-1 py-2 px-3 min-w-[80px]"
                data-testid={`vehicle-type-${type.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{type.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {VEHICLE_TYPES.map((type) => (
          <TabsContent key={type.id} value={type.id} className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <type.icon className="w-6 h-6" />
                  {type.label}
                </CardTitle>
                <CardDescription>{type.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Year *</label>
                    <Select value={selectedYear} onValueChange={setSelectedYear} disabled={loadingYears}>
                      <SelectTrigger data-testid="year-select">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {years.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Make *</label>
                    <Select value={selectedMake} onValueChange={setSelectedMake} disabled={!selectedYear || loadingMakes}>
                      <SelectTrigger data-testid="make-select">
                        <SelectValue placeholder={loadingMakes ? 'Loading...' : 'Make'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {makes.map((m) => (
                          <SelectItem key={m.make_id} value={m.make_name}>{m.make_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Model *</label>
                    <Select value={selectedModel} onValueChange={setSelectedModel} disabled={!selectedMake || loadingModels}>
                      <SelectTrigger data-testid="model-select">
                        <SelectValue placeholder={loadingModels ? 'Loading...' : 'Model'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {models.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">No models — type your own below</div>
                        ) : (
                          models.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Trim (optional)</label>
                    <Input
                      placeholder="e.g., XLE, Sport"
                      value={trim}
                      onChange={(e) => setTrim(e.target.value)}
                      data-testid="trim-input"
                    />
                  </div>
                </div>

                {/* Manual model input if not in list */}
                {selectedMake && selectedYear && models.length === 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Or enter model manually:</label>
                    <Input
                      placeholder="Enter model name"
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      data-testid="model-manual-input"
                    />
                  </div>
                )}

                {/* Inline VIN Decoder — auto-fills Year/Make/Model/Trim above */}
                <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 space-y-2" data-testid="inline-vin-decoder">
                  <label className="text-xs font-semibold uppercase tracking-wider text-sky-300 flex items-center gap-1">
                    <Hash className="w-3 h-3" /> VIN Decoder — auto-fill the fields above
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter 17-character VIN"
                      value={vin}
                      onChange={(e) => setVin(e.target.value.toUpperCase())}
                      maxLength={17}
                      className="font-mono text-sm"
                      data-testid="vin-input"
                    />
                    <Button
                      onClick={handleVinDecode}
                      disabled={vin.length !== 17 || loadingVin}
                      className="btn-gradient-info"
                      data-testid="decode-vin-button"
                      size="sm"
                    >
                      {loadingVin ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Decode</>}
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={handleLookup}
                    disabled={!selectedYear || !selectedMake || !selectedModel || loadingLookup}
                    className="btn-gradient-primary"
                    data-testid="lookup-button"
                  >
                    {loadingLookup ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get Full Vehicle Data
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline">Reset</Button>
                  {vehicleData && (
                    <Button onClick={handleSaveVehicle} className="btn-gradient-success" data-testid="save-vehicle">
                      <Bookmark className="w-4 h-4 mr-2" />
                      Save to Database
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Vehicle Data Display */}
      {loadingLookup && (
        <Card>
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
            <p className="font-semibold">Analyzing vehicle with AI...</p>
            <p className="text-sm text-muted-foreground mt-1">
              Generating comprehensive locksmith data (15-30 seconds)
            </p>
          </CardContent>
        </Card>
      )}

      {vehicleData && !loadingLookup && (
        <>
          {/* Vehicle Header */}
          <Card className="bg-gradient-to-br from-primary/10 to-purple-500/5 border-primary/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold">
                    {selectedYear} {selectedMake} {selectedModel}
                  </h2>
                  {trim && <p className="text-sm text-muted-foreground">{trim}</p>}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Badge variant="secondary" className="capitalize">{vehicleType}</Badge>
                    {vehicleData.from_cache && <Badge variant="outline">📦 Cached</Badge>}
                    {vehicleData.data_source === 'ai_generated' && (
                      <Badge variant="outline" className="bg-yellow-500/10 border-yellow-500/30 text-yellow-500">
                        ✨ AI Generated
                      </Badge>
                    )}
                    {vehicleData.key_type && (
                      <Badge variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-500">
                        🔑 {vehicleData.key_type}
                      </Badge>
                    )}
                    {vehicleData.estimated_time_minutes && (
                      <Badge variant="outline">⏱ {vehicleData.estimated_time_minutes} min</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Tech Mode toggle */}
                  <div className="flex items-center gap-2">
                    <Switch
                      id="tech-mode-toggle"
                      checked={techMode}
                      onCheckedChange={(v) => {
                        setTechMode(v);
                        try { localStorage.setItem('vl_tech_mode', v ? '1' : '0'); } catch (_) {}
                      }}
                      data-testid="tech-mode-toggle"
                    />
                    <Label htmlFor="tech-mode-toggle" className="cursor-pointer text-sm flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5 text-yellow-400" /> Tech Mode
                    </Label>
                  </div>
                  {vehicleData.estimated_profit && (
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Est. Profit</p>
                      <p className="text-3xl font-bold text-green-500">
                        ${vehicleData.estimated_profit?.toFixed(0)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tech Mode hero (curbside view) — only when toggled on */}
          {techMode && <TechModeCard vehicle={vehicleData} />}

          {/* Full data — sticky tab navigation (always shown so tech can dig deeper) */}
          <VehicleInfoTabs vehicle={vehicleData} />
        </>
      )}
    </div>
  );
}
